import { z } from 'zod';
import { objectId, timestampFields, FieldMetadata } from './base';

/**
 * User Schema
 * Represents an application user
 */

// Facebook auth sub-schema
export const FacebookAuthSchema = z.object({
  id: z.string().optional(),
  accessToken: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  displayName: z.string().optional(),
  name: z.string().optional(),
  email: z.string().optional(),
});

export type FacebookAuth = z.infer<typeof FacebookAuthSchema>;

export const UserSchema = z.object({
  _id: objectId,
  // Facebook auth
  fb: FacebookAuthSchema.optional(),
  // Contact info
  phone: z.string().optional(),
  address: z.string().optional(),
  address2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  // Auth
  provider: z.string().optional(),
  providerId: z.string().optional(),
  resetPasswordToken: z.string().optional(),
  resetPasswordExpires: z.coerce.date().optional(),
  email: z.string().optional(),
  name: z.string().optional(),
  password: z.string().optional(),
  // App data
  uploaded: z.boolean().optional(),
  // Refs
  players: z.array(z.string()).default([]),
  // Organizations the user follows in the mobile app (INT-0032) — public,
  // frictionless, powers Discover + the followed-games schedule merge.
  // Same shape/usage as the `players` watchlist above ($addToSet/$pull).
  followedOrganizations: z.array(z.string()).default([]),
  ...timestampFields,
});

export type User = z.infer<typeof UserSchema>;

// Metadata for User fields (for Mongoose refs and UI)
export const userMetadata: Record<string, FieldMetadata> = {
  _id: { hidden: true },
  fb: { hidden: true },
  phone: { label: 'Phone', editable: true },
  address: { label: 'Address', editable: true },
  address2: { label: 'Address 2', editable: true },
  city: { label: 'City', editable: true },
  state: { label: 'State', editable: true },
  zip: { label: 'Zip', editable: true },
  provider: { hidden: true },
  providerId: { hidden: true },
  resetPasswordToken: { hidden: true },
  resetPasswordExpires: { hidden: true },
  email: { label: 'Email', editable: true },
  name: { label: 'Name', editable: true },
  password: { hidden: true },
  uploaded: { hidden: true },
  players: { ref: 'Player', label: 'Players' },
  followedOrganizations: { ref: 'Organization', label: 'Followed Organizations' },
  created: { hidden: true },
  updated: { hidden: true },
};
